/**
 * Pure helpers behind the custom-role editor (`routes/admin/RoleEditorDrawer`).
 * Keys follow `module.entity.action`; the catalog served by
 * `GET permissions/catalog` is grouped by module, the editor splits each group
 * further by entity so an admin can tick single actions (e.g. only
 * `inventory.stock.read`) instead of a whole module.
 */

export interface EntityKeyGroup {
  readonly entity: string;
  readonly keys: readonly string[];
}

const ACTION_LABEL: Readonly<Record<string, string>> = {
  read: 'مشاهده',
  view: 'مشاهده',
  create: 'ایجاد',
  update: 'ویرایش',
  delete: 'حذف',
  approve: 'تأیید',
  submit: 'ارسال',
  post: 'ثبت قطعی',
  import: 'درون‌ریزی',
  export: 'برون‌ریزی',
  manage: 'مدیریت',
  assign: 'تخصیص',
  release: 'آزادسازی',
};

export function permissionKeyEntity(key: string): string {
  return key.split('.')[1] ?? key;
}

export function permissionKeyAction(key: string): string {
  return key.split('.').slice(2).join('.') || key;
}

export function permissionActionLabel(key: string): string {
  const action = permissionKeyAction(key);
  return ACTION_LABEL[action] ?? action;
}

export function groupKeysByEntity(keys: readonly string[]): readonly EntityKeyGroup[] {
  const byEntity = new Map<string, string[]>();
  for (const key of keys) {
    const entity = permissionKeyEntity(key);
    const list = byEntity.get(entity) ?? [];
    list.push(key);
    byEntity.set(entity, list);
  }
  return [...byEntity].map(([entity, entityKeys]) => ({ entity, keys: entityKeys }));
}

export function toggleKey(selected: ReadonlySet<string>, key: string): ReadonlySet<string> {
  const next = new Set(selected);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

/** Turns every key on unless they are all already on, in which case turns them all off. */
export function toggleKeys(
  selected: ReadonlySet<string>,
  keys: readonly string[],
): ReadonlySet<string> {
  const next = new Set(selected);
  const allOn = keys.length > 0 && keys.every((key) => next.has(key));
  for (const key of keys) {
    if (allOn) next.delete(key);
    else next.add(key);
  }
  return next;
}

export function sameKeySet(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) return false;
  for (const key of a) if (!b.has(key)) return false;
  return true;
}

export interface KeyDiff {
  readonly added: readonly string[];
  readonly removed: readonly string[];
}

export function diffKeys(before: ReadonlySet<string>, after: ReadonlySet<string>): KeyDiff {
  return {
    added: [...after].filter((key) => !before.has(key)).sort(),
    removed: [...before].filter((key) => !after.has(key)).sort(),
  };
}
